/**
 * face-api.js Model Loader
 * Loads TensorFlow.js face recognition models for proper embeddings
 * 
 * Supports offline mode by utilizing service worker cached models
 */

let modelsLoaded = false;
let loadingPromise: Promise<boolean> | null = null;

// Cache name must match service worker's FACEAPI_CACHE constant
// IMPORTANT: Keep in sync with public/sw.js
const FACEAPI_CACHE_NAME = 'faceapi-models-v7';

// Model base URL - always use same-origin static assets so both authenticated and
// public biometric pages share the same cached model source.
const LOCAL_MODEL_BASE_URL = '/models';

const getModelBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/biometric/')) {
    console.log('[FaceAPI] Public biometric page - using app-hosted models:', LOCAL_MODEL_BASE_URL);
  }

  return LOCAL_MODEL_BASE_URL;
};

// Model files that need to be available with expected sizes for validation
const MODEL_FILES = [
  'ssd_mobilenetv1_model-weights_manifest.json',
  'ssd_mobilenetv1_model-shard1',
  'ssd_mobilenetv1_model-shard2',
  'face_landmark_68_model-weights_manifest.json',
  'face_landmark_68_model-shard1',
  'face_recognition_model-weights_manifest.json',
  'face_recognition_model-shard1',
  'face_recognition_model-shard2'
];

// Expected minimum file sizes to detect corrupted/truncated downloads
const MODEL_FILE_MIN_SIZES: Record<string, number> = {
  'ssd_mobilenetv1_model-weights_manifest.json': 20000,  // ~26KB
  'ssd_mobilenetv1_model-shard1': 4000000,               // ~4MB
  'ssd_mobilenetv1_model-shard2': 1400000,               // ~1.4MB
  'face_landmark_68_model-weights_manifest.json': 5000,  // ~8KB
  'face_landmark_68_model-shard1': 350000,               // ~357KB
  'face_recognition_model-weights_manifest.json': 15000, // ~18KB
  'face_recognition_model-shard1': 4000000,              // ~4MB
  'face_recognition_model-shard2': 2200000,              // ~2.2MB
};

function isCacheEntryMissingError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'NotFoundError') {
    return true;
  }

  const message = error instanceof Error ? error.message : String(error);
  return message.includes('Entry was not found');
}

async function putCacheEntryWithRecovery(
  cacheName: string,
  request: RequestInfo | URL,
  response: Response,
  context: string,
): Promise<boolean> {
  const attemptWrite = async () => {
    const cache = await caches.open(cacheName);
    await cache.put(request, response.clone());
  };

  try {
    await attemptWrite();
    return true;
  } catch (error) {
    if (!isCacheEntryMissingError(error)) {
      console.warn(`[FaceAPI] Failed to cache ${context}:`, error);
      return false;
    }

    console.warn(`[FaceAPI] Cache backing store missing during ${context}, reopening and retrying once...`);

    try {
      await attemptWrite();
      return true;
    } catch (retryError) {
      console.warn(`[FaceAPI] Retry failed while caching ${context}:`, retryError);
      return false;
    }
  }
}

/**
 * Check if models are available in cache (for offline mode)
 * Also validates file sizes to detect corrupted downloads
 */
async function checkModelsInCache(): Promise<boolean> {
  if (typeof caches === 'undefined') {
    console.warn('[FaceAPI] Cache API not available');
    return false;
  }
  
  try {
    const cache = await caches.open(FACEAPI_CACHE_NAME);
    let foundCount = 0;
    const missing: string[] = [];
    const corrupted: string[] = [];
    
    console.log('[FaceAPI] Checking cache for models...');
    
    for (const file of MODEL_FILES) {
      const url = `/models/${file}`;
      const response = await cache.match(url);
      if (response) {
        // Validate file size to detect corrupted/truncated downloads
        const minSize = MODEL_FILE_MIN_SIZES[file] || 0;
        const contentLength = response.headers.get('content-length');
        const size = contentLength ? parseInt(contentLength, 10) : 0;
        
        // If we can't get size from headers, try to get it from the response body
        if (!size && minSize > 0) {
          try {
            const clonedResponse = response.clone();
            const blob = await clonedResponse.blob();
            if (blob.size < minSize) {
              corrupted.push(`${file} (${blob.size} < ${minSize} bytes)`);
              console.error(`[FaceAPI] ❌ Corrupted (too small): ${file} - ${blob.size} bytes, expected at least ${minSize}`);
              continue;
            }
          } catch (e) {
            console.warn(`[FaceAPI] Could not validate size of ${file}:`, e);
          }
        } else if (size > 0 && size < minSize) {
          corrupted.push(`${file} (${size} < ${minSize} bytes)`);
          console.error(`[FaceAPI] ❌ Corrupted (too small): ${file} - ${size} bytes, expected at least ${minSize}`);
          continue;
        }
        
        foundCount++;
        console.log(`[FaceAPI] ✅ Found in cache: ${file}`);
      } else {
        missing.push(file);
        console.warn(`[FaceAPI] ❌ Missing from cache: ${file}`);
      }
    }
    
    console.log(`[FaceAPI] Cache check: ${foundCount}/${MODEL_FILES.length} models found (cache: ${FACEAPI_CACHE_NAME})`);
    
    if (missing.length > 0) {
      console.error('[FaceAPI] Missing models:', missing);
    }
    
    if (corrupted.length > 0) {
      console.error('[FaceAPI] Corrupted models (will re-download):', corrupted);
      // Delete corrupted files from cache so they'll be re-downloaded
      for (const file of MODEL_FILES) {
        if (corrupted.some(c => c.startsWith(file))) {
          await cache.delete(`/models/${file}`);
          console.log(`[FaceAPI] Deleted corrupted cache entry: ${file}`);
        }
      }
      return false;
    }
    
    return foundCount === MODEL_FILES.length;
  } catch (error) {
    console.error('[FaceAPI] Cache check failed:', error);
    return false;
  }
}

// Track if cache has been validated this session (avoid repeated validation)
let cacheValidatedThisSession = false;

/**
 * Pre-cache models if not already cached
 * Uses quick header checks to avoid slow blob reads
 */
async function ensureModelsInCache(): Promise<boolean> {
  if (typeof caches === 'undefined') return false;
  
  // Skip if already validated this session
  if (cacheValidatedThisSession) {
    console.log('[FaceAPI] Cache already validated this session, skipping');
    return true;
  }
  
  try {
    const cache = await caches.open(FACEAPI_CACHE_NAME);
    const filesToDownload: string[] = [];
    
    // Quick check using headers first (fast)
    for (const file of MODEL_FILES) {
      const url = `/models/${file}`;
      const existing = await cache.match(url);
      
      if (!existing) {
        filesToDownload.push(file);
        continue;
      }
      
      // Quick size check via header (no blob read needed)
      const minSize = MODEL_FILE_MIN_SIZES[file] || 0;
      if (minSize > 0) {
        const contentLength = existing.headers.get('content-length');
        if (contentLength && parseInt(contentLength, 10) < minSize) {
          console.warn(`[FaceAPI] Cached ${file} too small, will re-download`);
          await cache.delete(url);
          filesToDownload.push(file);
        }
      }
    }
    
    // Only download missing files
    if (filesToDownload.length === 0) {
      console.log('[FaceAPI] All models already in cache');
      cacheValidatedThisSession = true;
      return true;
    }
    
    console.log(`[FaceAPI] Downloading ${filesToDownload.length} missing models...`);
    
    for (const file of filesToDownload) {
      const url = `/models/${file}`;
      const minSize = MODEL_FILE_MIN_SIZES[file] || 0;
      
      console.log(`[FaceAPI] Downloading model: ${file}`);
      try {
        const response = await fetch(url);
        if (response.ok) {
          // Only validate size for critical shard files
          if (minSize > 1000000) { // Only validate files > 1MB
            const clonedResponse = response.clone();
            const blob = await clonedResponse.blob();
            
            if (blob.size < minSize) {
              console.error(`[FaceAPI] ❌ Downloaded ${file} is corrupted (${blob.size} < ${minSize})`);
              continue;
            }
            
            const validResponse = new Response(blob, {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers
            });
            const stored = await putCacheEntryWithRecovery(FACEAPI_CACHE_NAME, url, validResponse, file);
            if (stored) {
              console.log(`[FaceAPI] ✅ Cached: ${file} (${blob.size} bytes)`);
            }
          } else {
            // Small files: cache directly without validation
            const stored = await putCacheEntryWithRecovery(FACEAPI_CACHE_NAME, url, response, file);
            if (stored) {
              console.log(`[FaceAPI] ✅ Cached: ${file}`);
            }
          }
        } else {
          console.error(`[FaceAPI] ❌ Failed to download ${file}: ${response.status}`);
        }
      } catch (fetchError) {
        console.warn(`[FaceAPI] Failed to cache ${file}:`, fetchError);
      }
    }
    
    cacheValidatedThisSession = true;
    return await checkModelsInCache();
  } catch (error) {
    console.error('[FaceAPI] Failed to ensure models in cache:', error);
    return false;
  }
}

/**
 * Custom fetch function that tries cache first when offline
 * This helps face-api.js work offline by serving from cache
 */
async function offlineAwareFetch(url: string): Promise<Response> {
  console.log('[FaceAPI] offlineAwareFetch called for:', url);
  
  // Extract model filename from URL (handles both relative and absolute URLs)
  const modelFileName = url.split('/').pop() || '';
  const relativePath = `/models/${modelFileName}`;
  
  // Try network first if online
  if (navigator.onLine) {
    try {
      console.log('[FaceAPI] Trying network fetch:', url);
      const response = await fetch(url);
      if (response.ok) {
        // Cache for offline use (store with relative path for consistency)
        try {
          const stored = await putCacheEntryWithRecovery(FACEAPI_CACHE_NAME, relativePath, response, relativePath);
          if (stored) {
            console.log('[FaceAPI] Cached model:', relativePath);
          }
        } catch (cacheError) {
          console.warn('[FaceAPI] Failed to cache:', cacheError);
        }
        return response;
      }
    } catch (e) {
      console.log('[FaceAPI] Network failed, trying cache:', e);
    }
  }
  
  // Try cache (offline or network failed)
  // Try multiple URL variations to maximize cache hit rate
  const cache = await caches.open(FACEAPI_CACHE_NAME);
  
  // Try 1: Exact URL match
  let cachedResponse = await cache.match(url);
  if (cachedResponse) {
    console.log('[FaceAPI] ✅ Cache hit (exact):', url);
    return cachedResponse;
  }
  
  // Try 2: Relative path (most common in cache)
  cachedResponse = await cache.match(relativePath);
  if (cachedResponse) {
    console.log('[FaceAPI] ✅ Cache hit (relative):', relativePath);
    return cachedResponse;
  }
  
  // Try 3: Without leading slash
  cachedResponse = await cache.match(relativePath.substring(1));
  if (cachedResponse) {
    console.log('[FaceAPI] ✅ Cache hit (no slash):', relativePath.substring(1));
    return cachedResponse;
  }
  
  console.error('[FaceAPI] ❌ Model not in cache:', modelFileName);
  throw new Error(`Model not available offline: ${modelFileName}`);
}

export async function loadFaceApiModels(): Promise<boolean> {
  // If already loaded, return immediately
  if (modelsLoaded) {
    return true;
  }

  // If loading is in progress, wait for it
  if (loadingPromise) {
    return loadingPromise;
  }

  // Start loading
  loadingPromise = (async () => {
    try {
      console.log('[FaceAPI] Loading face recognition models...');
      
      // Check online status
      const isOnline = navigator.onLine;
      console.log(`[FaceAPI] Network status: ${isOnline ? 'online' : 'offline'}`);
      
      // If online, ensure models are cached for future offline use
      if (isOnline) {
        await ensureModelsInCache();
      } else {
        // If offline, verify models are in cache first
        const inCache = await checkModelsInCache();
        if (!inCache) {
          console.error('[FaceAPI] ❌ Models not available in cache for offline use');
          throw new Error('Face recognition models not available offline. Please connect to internet first.');
        }
        console.log('[FaceAPI] ✅ Models available in cache for offline use');
      }
      
      const faceapi = await import('@vladmandic/face-api');

      // CRITICAL: Force WASM backend instead of WebGL.
      // The WebGL backend on certain mobile GPUs (Mali/Adreno) silently produces
      // identical descriptors for different faces — a model malfunction that is
      // undetectable by the caller (no error thrown, output has normal variance).
      // WASM is CPU-based and deterministic across all devices, eliminating this issue.
      // Trade-off: ~2-3x slower than WebGL, but for single-face enrollment this is fine.
      try {
        const tf = (faceapi as unknown as { tf: {
          setWasmPaths: (path: string) => void;
          setBackend: (backend: string) => Promise<boolean>;
          ready: () => Promise<void>;
          getBackend: () => string;
        } }).tf;
        if (tf?.setWasmPaths && tf?.setBackend && tf?.ready) {
          tf.setWasmPaths('/tfjs-wasm/');
          const wasmOk = await tf.setBackend('wasm');
          await tf.ready();
          const activeBackend = tf.getBackend();
          if (wasmOk && activeBackend === 'wasm') {
            console.log('[FaceAPI] ✅ WASM backend activated:', activeBackend);
          } else {
            // WASM backend failed to activate — this produces inconsistent descriptors
            // on mobile GPUs. Throw so the caller can surface a meaningful error
            // instead of silently using WebGL which causes face match failures.
            throw new Error(
              `WASM backend activation failed. setBackend returned ${wasmOk}, active backend: ${activeBackend}. ` +
              `WebGL fallback would produce inconsistent descriptors on some devices.`
            );
          }
        } else {
          console.warn('[FaceAPI] tf.setBackend not available, using default (WebGL) backend');
        }
      } catch (backendErr) {
        console.error('[FaceAPI] ❌ Failed to set WASM backend:', backendErr);
        throw backendErr;
      }

      // CRITICAL: Override the global fetch for face-api.js model loading when offline
      // face-api.js uses fetch internally, and we need to serve from cache when offline
      const originalFetch = globalThis.fetch;
      
      if (!navigator.onLine) {
        console.log('[FaceAPI] Overriding fetch for offline model loading...');
        globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
          const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
          
          // Only intercept model file requests
          if (url.includes('/models/')) {
            return offlineAwareFetch(url);
          }
          
          // For other requests, use original fetch
          return originalFetch(input, init);
        };
      }
      
      try {
        // Model path - Use API server for public biometric pages, relative path for authenticated pages
        const MODEL_URL = getModelBaseUrl();
        console.log('[FaceAPI] Model URL:', MODEL_URL, '(offline mode:', !navigator.onLine, ')');
        
        // Skip pre-validation when online - validation already happened in ensureModelsInCache
        // Only validate offline to catch cache corruption (using HEAD request for speed)
        if (!navigator.onLine) {
          console.log('[FaceAPI] Offline: quick-validating cached models...');
          const cache = await caches.open(FACEAPI_CACHE_NAME);
          
          const quickValidate = async (fileName: string, minSize: number): Promise<void> => {
            const cached = await cache.match(`/models/${fileName}`);
            if (!cached) {
              throw new Error(`Model not in cache: ${fileName}`);
            }
            // Check content-length header (fast) instead of fetching full blob
            const contentLength = cached.headers.get('content-length');
            if (contentLength && parseInt(contentLength, 10) < minSize) {
              throw new Error(`Cached model corrupted: ${fileName}`);
            }
          };
          
          await quickValidate('ssd_mobilenetv1_model-shard1', 4000000);
          await quickValidate('ssd_mobilenetv1_model-shard2', 1400000);
          console.log('[FaceAPI] ✅ Cached models validated');
        }
        
        // Load required models for face recognition
        console.log('[FaceAPI] Loading ssdMobilenetv1...');
        await faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL);
        
        console.log('[FaceAPI] Loading faceLandmark68Net...');
        await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        
        console.log('[FaceAPI] Loading faceRecognitionNet...');
        await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
        
        modelsLoaded = true;
        console.log('[FaceAPI] ✅ All models loaded successfully');
        return true;
      } finally {
        // Restore original fetch
        if (!navigator.onLine) {
          globalThis.fetch = originalFetch;
          console.log('[FaceAPI] Restored original fetch after offline loading');
        }
      }
    } catch (error: unknown) {
      console.error('[FaceAPI] Failed to load models:', error);
      modelsLoaded = false;
      loadingPromise = null;
      
      // Provide helpful error message
      if (!navigator.onLine) {
        throw new Error('Face recognition models not available offline. Visit this page while online first to cache them.');
      }
      throw error;
    }
  })();

  try {
    const result = await loadingPromise;
    loadingPromise = null;
    return result;
  } catch (error) {
    loadingPromise = null;
    throw error;
  }
}

export function areFaceApiModelsLoaded(): boolean {
  return modelsLoaded;
}

/**
 * Force reload models (useful after cache update)
 */
export async function reloadFaceApiModels(): Promise<boolean> {
  modelsLoaded = false;
  loadingPromise = null;
  return loadFaceApiModels();
}

/**
 * Validate that face-api.js models are not just loaded, but actually working
 * This tests that the models can generate a proper descriptor
 */
export async function validateFaceApiModels(): Promise<boolean> {
  if (!modelsLoaded) {
    console.warn('[FaceAPI] Models not loaded yet');
    return false;
  }

  try {
    const faceapi = await import('@vladmandic/face-api');
    
    // Check all required nets are loaded
    if (!faceapi.nets.ssdMobilenetv1.isLoaded) {
      console.error('[FaceAPI] ssdMobilenetv1 not loaded');
      return false;
    }
    if (!faceapi.nets.faceLandmark68Net.isLoaded) {
      console.error('[FaceAPI] faceLandmark68Net not loaded');
      return false;
    }
    if (!faceapi.nets.faceRecognitionNet.isLoaded) {
      console.error('[FaceAPI] faceRecognitionNet not loaded');
      return false;
    }
    
    console.log('[FaceAPI] ✅ All models are loaded and ready');
    return true;
  } catch (error) {
    console.error('[FaceAPI] Model validation failed:', error);
    return false;
  }
}

/**
 * Preload the face-api.js library WITHOUT loading models
 * This ensures the JavaScript chunks are cached by the service worker
 * MUST be called while online to cache the dynamic import chunks
 */
export async function preloadFaceApiLibrary(): Promise<boolean> {
  console.log('[FaceAPI] Pre-loading face-api.js library (for chunk caching)...');
  
  try {
    // Dynamic import triggers chunk loading and caching
    const faceapi = await import('@vladmandic/face-api');
    
    // Verify the import succeeded
    if (faceapi && faceapi.nets) {
      console.log('[FaceAPI] ✅ Library pre-loaded successfully');
      return true;
    }
    
    console.warn('[FaceAPI] Library loaded but seems incomplete');
    return false;
  } catch (error) {
    console.error('[FaceAPI] Failed to pre-load library:', error);
    return false;
  }
}
